<script lang="ts">
	import { metadata } from '$lib/content/data/metadata';
	import { getOtherCourses } from '$lib/content/data/courses';
	import { getCohortCourseBySlug } from '$lib/content/data/cohort-courses';
	import CurrencySelector from '$lib/components/CurrencySelector.svelte';
	import LocalSessionTime from '$lib/components/LocalSessionTime.svelte';
	import TestimonialCard from '$lib/components/TestimonialCard.svelte';
	import type { PageData } from './$types';
	import { pricingState } from '$lib/stores/pricing.svelte';
	import { getPricing, type CurrencyCode } from '$lib/utils/pricing';
	import { onMount } from 'svelte';
	import { browser } from '$app/environment';

	let { data }: { data: PageData } = $props();
	// Accordion state for curriculum
	let expandedModule = $state<number | null>(null);

	function toggleModule(index: number) {
		expandedModule = expandedModule === index ? null : index;
	}
	let course = $derived(data.course);
	let otherCourses = $derived(getOtherCourses(course.slug, 3));
	// Free live-session bridge — only shown on the matching paid course
	let freeSession = $derived(
		course.slug === 'agentic-ai-masterclass' ? getCohortCourseBySlug('free-agentic-ai-masterclass') : undefined
	);

	// Currency change handler
	function handleCurrencyChange(_currency: CurrencyCode) {
		// Handled by global state
	}

	onMount(() => {
		if (!browser) return;
	});

	const levelColors: Record<string, string> = {
		foundation: 'bg-ak-wintergreen-dim text-ak-wintergreen',
		basic: 'bg-ak-wintergreen/10 text-ak-wintergreen/80',
		advanced: 'bg-ak-wintergreen/20 text-ak-wintergreen'
	};

	const categoryLabels: Record<string, string> = {
		programming: 'Programming',
		'agentic-ai': 'Agentic AI'
	};

	// Calculate total lessons
	const totalLessons = $derived(
		course.curriculum?.reduce((acc, module) => acc + module.lessons.length, 0) ?? 0
	);

	// Course Schema (JSON-LD)
	const courseSchema = $derived({
		'@context': 'https://schema.org',
		'@type': 'Course',
		name: course.title,
		description: course.longDescription || course.description,
		provider: {
			'@type': 'Organization',
			name: 'Akonia Codex',
			url: metadata.company.website
		},
		image: `${metadata.company.website}/brand/og-image.optimized.png`,
		offers: {
			'@type': 'Offer',
			price: course.price,
			priceCurrency: course.currency || 'INR',
			category: course.category,
			availability: 'https://schema.org/InStock'
		},
		educationalLevel: course.level === 'foundation' ? 'Beginner' : course.level === 'basic' ? 'Intermediate' : 'Advanced',
		courseCode: course.id.toUpperCase(),
		abstract: course.longDescription || course.description,
		keywords: course.slug === 'web-gis-basic' ? 'web gis malaysia, webgis singapore, gis development india, gis course kerala, openlayers course, leaflet gis training, geospatial development, spatial mapping, gis programming kuala lumpur, web mapping bangalore, gis course kochi' : course.slug === 'agentic-ai-masterclass' ? 'agentic ai malaysia, ai development singapore, bmad method india, ai training kerala, spec-driven development, ai masterclass, prompt engineering, ai agents, ai training course' : 'programming malaysia, web development singapore, coding course india, ai course kerala'
	});

	// Generate enhanced meta title and description based on course type
	const enhancedTitle = $derived(
		course.slug === 'web-gis-basic'
			? 'Web GIS Development & OpenLayers Course | Akonia Codex'
			: course.slug === 'agentic-ai-masterclass'
				? 'Agentic AI Course: AI Agents & LLM Engineering'
				: `${course.title} | Programming Training | Akonia Codex`
	);

	const enhancedDescription = $derived(
		course.slug === 'web-gis-basic'
			? 'Learn Web GIS development using spec-driven methodology. Master OpenLayers, Leaflet, GeoJSON, and spatial APIs in 8 weeks.'
			: course.slug === 'agentic-ai-masterclass'
				? 'Master Agentic AI development and LLM engineering with the BMAD method — AI agents, spec-driven dev, and prompt engineering. Live training for developers in Malaysia, Singapore & India.'
				: (course.longDescription || course.description)
	);
</script>

<svelte:head>
	<title>{enhancedTitle}</title>
	<link rel="canonical" href="{metadata.company.website}/courses/{course.slug}" />
	<meta name="description" content={enhancedDescription} />
	<meta property="og:title" content={enhancedTitle} />
	<meta property="og:description" content={enhancedDescription} />
	<meta property="og:type" content="website" />
	<meta property="og:url" content="{metadata.company.website}/courses/{course.slug}" />
	<meta property="og:image" content={course.slug === 'agentic-ai-masterclass' ? `${metadata.company.website}/images/og/agentic-ai-masterclass.png` : `${metadata.company.website}/brand/og-image.optimized.png`} />
	<meta property="og:image:width" content="1200" />
	<meta property="og:image:height" content="630" />
	<meta property="og:image:alt" content="{course.title} - Akonia Codex Course" />
	<meta name="twitter:card" content="summary_large_image" />
	<meta name="twitter:title" content={enhancedTitle} />
	<meta name="twitter:description" content={enhancedDescription} />
	<meta name="twitter:image" content={course.slug === 'agentic-ai-masterclass' ? `${metadata.company.website}/images/og/agentic-ai-masterclass.png` : `${metadata.company.website}/brand/og-image.optimized.png`} />
	<meta name="twitter:image:alt" content="{course.title} - Akonia Codex Course" />

	<!-- Structured Data -->
	{@html `<script type="application/ld+json">${JSON.stringify(courseSchema)}</` + `script>`}

	<!-- BreadcrumbList Structured Data -->
	{@html `<script type="application/ld+json">${JSON.stringify({
		"@context": "https://schema.org",
		"@type": "BreadcrumbList",
		"itemListElement": [
			{
				"@type": "ListItem",
				"position": 1,
				"name": "Home",
				"item": metadata.company.website
			},
			{
				"@type": "ListItem",
				"position": 2,
				"name": "Training",
				"item": `${metadata.company.website}/courses`
			},
			{
				"@type": "ListItem",
				"position": 3,
				"name": course.title,
				"item": `${metadata.company.website}/courses/${course.slug}`
			}
		]
	})}</` + `script>`}
	
	<!-- FAQPage Structured Data -->
	{#if course}
		{@html `<script type="application/ld+json">${JSON.stringify({
			"@context": "https://schema.org",
			"@type": "FAQPage",
			"mainEntity": [
				{
					"@type": "Question",
					"name": `What is the schedule for ${course.title}?`,
					"acceptedAnswer": {
						"@type": "Answer",
						"text": `Weekend live sessions. Duration: ${course.duration}. Contact for batch timings.`
					}
				},
				{
					"@type": "Question",
					"name": course.prerequisites && course.prerequisites.length > 0
						? `What are the prerequisites for ${course.title}?`
						: `Do I need prior coding experience for ${course.title}?`,
					"acceptedAnswer": {
						"@type": "Answer",
						"text": course.prerequisites && course.prerequisites.length > 0
							? `Yes. ${course.prerequisites.slice(0, 2).join(' and ')}.`
							: `No prior experience required. ${course.level === 'foundation' ? 'Beginner-friendly.' : 'For developers.'}`
					}
				},
				{
					"@type": "Question",
					"name": `What will I learn in ${course.title}?`,
					"acceptedAnswer": {
						"@type": "Answer",
						"text": `${course.features.slice(0, 3).join(', ')}. ${course.level} level course.`
					}
				},
				{
					"@type": "Question",
					"name": "Do I get a certificate?",
					"acceptedAnswer": {
						"@type": "Answer",
						"text": `Yes. Certificate upon completion of ${course.title}.`
					}
				}
			]
		})}</` + `script>`}
	{/if}

	<!-- BreadcrumbList Structured Data -->
	{@html `<script type="application/ld+json">${JSON.stringify({
		'@context': 'https://schema.org',
		'@type': 'BreadcrumbList',
		itemListElement: [
			{
				'@type': 'ListItem',
				position: 1,
				name: 'Home',
				item: metadata.company.website
			},
			{
				'@type': 'ListItem',
				position: 2,
				name: 'Courses',
				item: `${metadata.company.website}/courses`
			},
			{
				'@type': 'ListItem',
				position: 3,
				name: course.title,
				item: `${metadata.company.website}/courses/${course.slug}`
			}
		]
	})}</` + `script>`}
</svelte:head>

<main id="main-content" class="pb-24 lg:pb-0">
	<!-- Hero Section -->
	<section class="relative py-12 lg:py-20 overflow-hidden border-b border-ak-border-subtle">
		<div class="absolute inset-0 bg-linear-dark"></div>
		{#if course.highlighted}
			<div class="absolute top-0 left-1/2 -translate-x-1/2 w-150 h-75 bg-linear-glow opacity-30 blur-3xl pointer-events-none"></div>
		{/if}

		<div class="relative container mx-auto px-4">
			<!-- Breadcrumb -->
			<nav class="mb-6 text-sm">
				<ol class="flex items-center gap-2 text-ak-text-muted">
					<li><a href="/" class="hover:text-ak-wintergreen transition-colors">Home</a></li>
					<li><span class="mx-1">/</span></li>
					<li><a href="/courses" class="hover:text-ak-wintergreen transition-colors">Courses</a></li>
					<li><span class="mx-1">/</span></li>
					<li class="text-ak-text-secondary">{course.title}</li>
				</ol>
			</nav>

			<div class="grid lg:grid-cols-3 gap-8 lg:gap-12">
				<!-- Course Info -->
				<div class="lg:col-span-2">
					<!-- Badges -->
					<div class="flex flex-wrap items-center gap-3 mb-6">
						<span class="px-3 py-1 text-xs font-medium rounded-full {levelColors[course.level]}">
							{course.level.charAt(0).toUpperCase() + course.level.slice(1)}
						</span>
						<span class="px-3 py-1 text-xs font-medium rounded-full bg-ak-wintergreen-dim text-ak-wintergreen">
							{categoryLabels[course.category]}
						</span>
						{#if course.highlighted}
							<span class="px-3 py-1 text-xs font-bold rounded-full bg-ak-wintergreen text-ak-deep">
								FEATURED
							</span>
						{/if}
					</div>

					<h1 class="font-display font-bold mb-4">
						{course.title}
					</h1>

					<p class="text-lg text-ak-text-secondary leading-relaxed mb-6">
						{course.longDescription || course.description}
					</p>

					<!-- Quick Stats -->
					<div class="flex flex-wrap gap-6 text-sm">
						<div class="flex items-center gap-2 text-ak-text-muted">
							<svg class="w-5 h-5 text-ak-wintergreen" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
								<path stroke-linecap="round" stroke-linejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
							</svg>
							<span>{course.duration}</span>
						</div>
						{#if totalLessons > 0}
							<div class="flex items-center gap-2 text-ak-text-muted">
								<svg class="w-5 h-5 text-ak-wintergreen" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
									<path stroke-linecap="round" stroke-linejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
								</svg>
								<span>2 classes/week</span>
							</div>
						{/if}
						<div class="flex items-center gap-2 text-ak-text-muted">
							<svg class="w-5 h-5 text-ak-wintergreen" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
								<path stroke-linecap="round" stroke-linejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
							</svg>
							<span>Instructor-led</span>
						</div>
					</div>

				<!-- Target Audience & Prerequisites -->
					<div class="mt-8 space-y-6">
						<!-- Who This Course Is For -->
						<div>
							<h2 class="font-semibold mb-3 flex items-center gap-2">
								<svg class="w-5 h-5 text-ak-wintergreen" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
									<path stroke-linecap="round" stroke-linejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
								</svg>
								Who This Course Is For
							</h2>
							<ul class="space-y-2">
								{#each course.targetAudience || [] as audience}
									<li class="flex items-start gap-2 text-sm text-ak-text-secondary">
										<svg class="w-4 h-4 text-ak-wintergreen mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
											<path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
										</svg>
										<span>{audience}</span>
									</li>
								{/each}
							</ul>
						</div>

						<!-- Prerequisites -->
						<div>
							<h2 class="font-semibold mb-3 flex items-center gap-2">
								<svg class="w-5 h-5 text-ak-wintergreen" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
									<path stroke-linecap="round" stroke-linejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
								</svg>
								Prerequisites
							</h2>
							{#if course.prerequisites && course.prerequisites.length > 0}
								<ul class="space-y-2">
									{#each course.prerequisites as prereq}
										<li class="flex items-start gap-2 text-sm text-ak-text-secondary">
											<svg class="w-4 h-4 text-ak-wintergreen mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
												<path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
											</svg>
											<span>{prereq}</span>
										</li>
									{/each}
								</ul>
							{:else}
								<p class="text-sm text-ak-text-muted">
									No prerequisites required. This course is designed for beginners.
								</p>
							{/if}
						</div>

						<!-- Teaching Modes -->
						<div>
							<h2 class="font-semibold mb-3 flex items-center gap-2">
								<svg class="w-5 h-5 text-ak-wintergreen" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
									<path stroke-linecap="round" stroke-linejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
								</svg>
								Teaching Mode
							</h2>
							<div class="flex flex-wrap gap-2">
								{#each course.teachingModes || [] as mode}
									<span class="px-3 py-1.5 text-sm font-medium rounded-full bg-ak-surface border border-ak-border-subtle text-ak-text-secondary">
										{mode.name}
									</span>
								{/each}
							</div>
						</div>
					</div>
				</div>

				<!-- Pricing Card (desktop only) -->
				<div class="hidden lg:block lg:col-span-1">
					<div class="sticky top-24 p-4 sm:p-6 bg-linear-card rounded-lg border border-ak-border-subtle">
						<!-- Currency Selector -->
						<div class="flex justify-end mb-4">
							<CurrencySelector onCurrencyChange={handleCurrencyChange} />
						</div>

						<div class="text-center mb-6">
							<div class="text-sm text-ak-text-muted mb-1">Course Fee</div>
							<div class="font-display text-4xl font-bold text-ak-wintergreen">
								{getPricing(course.price, pricingState.selectedCurrency)}
							</div>
							<div class="text-sm text-ak-text-muted mt-1">+ GST</div>
						</div>

						<a
							href="/contact?type=training&course={course.slug}"
							class="block w-full py-3 min-h-12 bg-ak-wintergreen text-ak-deep font-semibold text-center rounded-md hover:opacity-90 transition-opacity mb-4"
						>
							Express Interest
						</a>

						<a
							href="/contact?type=training"
							class="block w-full py-3 border border-ak-border-subtle text-ak-text-secondary text-center rounded-md hover:border-ak-wintergreen transition-colors text-sm"
						>
							Ask a Question
						</a>

						{#if freeSession}
							<a
								href={freeSession.detailRedirect ?? `/courses/${freeSession.slug}`}
								class="block mt-4 p-3.5 rounded-lg bg-ak-wintergreen-dim/30 border border-ak-wintergreen/20 hover:border-ak-wintergreen/40 transition-colors"
							>
								<span class="block text-xs font-semibold text-ak-wintergreen uppercase tracking-wider mb-1">Free intro</span>
								<span class="block text-sm text-ak-text-secondary leading-snug">
									Not ready for 10 weeks? Try the free 90-minute live session &mdash; <LocalSessionTime cohort={freeSession.cohort} variant="date" />.
								</span>
							</a>
						{/if}

						<div class="mt-6 pt-6 border-t border-ak-border-subtle">
							<h3 class="text-sm font-semibold mb-3">What's Included</h3>
							<ul class="space-y-2 text-sm text-ak-text-secondary">
								<li class="flex items-center gap-2">
									<svg class="w-4 h-4 text-ak-wintergreen shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
										<path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
									</svg>
									Live instructor sessions
								</li>
								<li class="flex items-center gap-2">
									<svg class="w-4 h-4 text-ak-wintergreen shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
										<path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
									</svg>
									Hands-on projects
								</li>
								<li class="flex items-center gap-2">
									<svg class="w-4 h-4 text-ak-wintergreen shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
										<path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
									</svg>
									Course materials
								</li>
								<li class="flex items-center gap-2">
									<svg class="w-4 h-4 text-ak-wintergreen shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
										<path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
									</svg>
									Certificate of completion
								</li>
							</ul>
						</div>
					</div>
				</div>
			</div>
		</div>
	</section>

	<!-- What You'll Learn -->
	<section class="py-8 lg:py-12">
		<div class="container mx-auto px-4">
			<h2 class="font-display font-bold mb-2">What You'll Learn</h2>

			<div class="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
				{#each course.features as feature}
					<div class="flex items-start gap-3 p-4 bg-linear-card rounded-lg border border-ak-border-subtle hover:border-ak-wintergreen/50 transition-colors active:opacity-95">
						<svg class="w-5 h-5 text-ak-wintergreen mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
							<path stroke-linecap="round" stroke-linejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
						</svg>
						<span class="text-ak-text-secondary">{feature}</span>
					</div>
				{/each}
			</div>
		</div>
	</section>


	<!-- Testimonials -->
	<section class="py-8 lg:py-12 bg-ak-surface/30">
		<div class="container mx-auto px-4">
			<h2 class="font-display font-bold mb-2">What Our Students Say</h2>
			<p class="text-ak-text-secondary max-w-xl mb-8">Real feedback from developers who took this course.</p>

				<div class="grid md:grid-cols-3 gap-4">
					<TestimonialCard
						quote="This course completely changed how I approach development. The spec-driven methodology is a game-changer."
						name="Vikram K."
						subtitle="Software Developer"
						variant="stars"
						rating={5}
					/>
					<TestimonialCard
						quote="Finally understood how to properly use AI in development. The BMAD tool makes everything so much faster."
						name="Anita N."
						subtitle="Full-stack Developer"
						variant="stars"
						rating={5}
					/>
					<TestimonialCard
						quote="Went from complete beginner to building real apps in 10 weeks. The live sessions made all the difference."
						name="Rahul S."
						subtitle="Indie Developer"
						variant="stars"
						rating={5}
					/>
				</div>
		</div>
	</section>
	<!-- Agentic AI Showcase (only for AI courses) -->
	{#if course.category === 'agentic-ai'}
		<section class="py-8 lg:py-12">
			<div class="container mx-auto px-4">
				<!-- Foundation Module Highlight -->
				<div class="relative p-4 sm:p-6 rounded-lg bg-linear-to-br from-ak-wintergreen-dim/30 to-ak-surface border border-ak-wintergreen/20 mb-8 overflow-hidden">
					<div class="absolute top-0 right-0 w-64 h-64 bg-ak-wintergreen/10 rounded-full blur-3xl pointer-events-none"></div>

					<div class="relative">
						<div class="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-ak-wintergreen/20 text-ak-wintergreen text-xs font-semibold mb-4">
							<svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
								<path stroke-linecap="round" stroke-linejoin="round" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
							</svg>
							INCLUDED FOUNDATION MODULE
						</div>

						<h2 class="font-display font-bold mb-2">
							Start with the Foundation Module
						</h2>

						<p class="text-ak-text-secondary max-w-3xl mb-6 leading-relaxed">
							Every AI Masterclass includes our comprehensive Foundation Module — the essential groundwork that prepares you for AI-assisted development. Even if you've coded before, this module teaches you how to think and work with AI as your coding partner.
						</p>

							<div class="max-w-2xl">
								<div class="p-4 bg-ak-deep/30 rounded-lg flex items-start gap-3">
									<div class="w-10 h-10 rounded-lg bg-ak-wintergreen-dim flex items-center justify-center shrink-0">
										<svg class="w-6 h-6 text-ak-wintergreen" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
											<path stroke-linecap="round" stroke-linejoin="round" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
										</svg>
									</div>
									<div>
										<h3 class="font-semibold text-lg mb-2">Foundation Session (40 min)</h3>
										<p class="text-ak-text-secondary leading-relaxed">
											Claude Code setup, understanding AI capabilities, and prompt fundamentals — everything you need to start coding with AI in a single focused session.
										</p>
									</div>
								</div>
							</div>
					</div>
				</div>

				<!-- BMAD Methodology -->
				<div class="mb-12">
					<h2 class="font-display font-bold mb-2">The BMAD Methodology</h2>
					<p class="text-ak-text-secondary max-w-3xl mb-8 leading-relaxed">
						BMAD (BMAD Method for AI-Assisted Development) is our proprietary framework for building software with AI. It transforms AI from a novelty into a reliable, predictable development partner.
					</p>

					<div class="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
						<div class="relative p-4 sm:p-5 bg-linear-card rounded-lg border border-ak-border-subtle hover:border-ak-wintergreen/50 transition-colors">
							<span class="absolute -top-3 left-4 px-2 py-0.5 bg-ak-wintergreen text-ak-deep text-xs font-bold rounded">1</span>
							<h3 class="font-display text-lg font-semibold mt-2 mb-2">Brief</h3>
							<p class="text-sm text-ak-text-muted">Define your product vision with structured discovery</p>
						</div>

						<div class="relative p-4 sm:p-5 bg-linear-card rounded-lg border border-ak-border-subtle hover:border-ak-wintergreen/50 transition-colors">
							<span class="absolute -top-3 left-4 px-2 py-0.5 bg-ak-wintergreen text-ak-deep text-xs font-bold rounded">2</span>
							<h3 class="font-display text-lg font-semibold mt-2 mb-2">Plan</h3>
							<p class="text-sm text-ak-text-muted">Create detailed PRD and UX specifications</p>
						</div>

						<div class="relative p-4 sm:p-5 bg-linear-card rounded-lg border border-ak-border-subtle hover:border-ak-wintergreen/50 transition-colors">
							<span class="absolute -top-3 left-4 px-2 py-0.5 bg-ak-wintergreen text-ak-deep text-xs font-bold rounded">3</span>
							<h3 class="font-display text-lg font-semibold mt-2 mb-2">Architect</h3>
							<p class="text-sm text-ak-text-muted">Design technical architecture for AI implementation</p>
						</div>

						<div class="relative p-4 sm:p-5 bg-linear-card rounded-lg border border-ak-border-subtle hover:border-ak-wintergreen/50 transition-colors">
							<span class="absolute -top-3 left-4 px-2 py-0.5 bg-ak-wintergreen text-ak-deep text-xs font-bold rounded">4</span>
							<h3 class="font-display text-lg font-semibold mt-2 mb-2">Develop</h3>
							<p class="text-sm text-ak-text-muted">Implement with spec-driven, AI-assisted coding</p>
						</div>
					</div>
				</div>

				<!-- Spec-Driven Development Workflow -->
				<div class="p-4 sm:p-6 bg-ak-surface/50 rounded-lg border border-ak-border-subtle">
					<h2 class="font-display font-bold mb-2">Spec-Driven Development Workflow</h2>
					<p class="text-ak-text-secondary mb-6">
						Unlike traditional coding where you figure things out as you go, spec-driven development means you define exactly what you want before writing a single line of code. AI then implements to spec.
					</p>

					<div class="flex flex-col md:flex-row gap-4 items-start">
						{#each [
							{ step: 'Write', desc: 'Create detailed story specifications with acceptance criteria', icon: 'M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z' },
							{ step: 'Review', desc: 'AI reviews specs and identifies gaps before implementation', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4' },
							{ step: 'Build', desc: 'AI implements exactly what the spec describes', icon: 'M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4' }
						] as item, i}
							<div class="flex-1 flex items-start gap-4">
								<div class="w-10 h-10 rounded-lg bg-ak-wintergreen-dim flex items-center justify-center shrink-0">
									<svg class="w-6 h-6 text-ak-wintergreen" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
										<path stroke-linecap="round" stroke-linejoin="round" d={item.icon} />
									</svg>
								</div>
								<div>
									<h3 class="font-semibold mb-1">{item.step}</h3>
									<p class="text-sm text-ak-text-muted">{item.desc}</p>
								</div>
							</div>
							{#if i < 2}
								<div class="hidden md:flex items-center justify-center w-8 shrink-0">
									<svg class="w-5 h-5 text-ak-wintergreen" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
										<path stroke-linecap="round" stroke-linejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
									</svg>
								</div>
							{/if}
						{/each}
					</div>

					<div class="mt-6 pt-6 border-t border-ak-border-subtle">
						<p class="text-sm text-ak-wintergreen font-medium">
							✓ Result: 4-6x faster development with consistent, maintainable code
						</p>
					</div>
				</div>
			</div>
		</section>
	{/if}

	<!-- Curriculum -->
	{#if course.curriculum && course.curriculum.length > 0}
		<section class="py-8 lg:py-12">
			<div class="container mx-auto px-4">
				<h2 class="font-display font-bold mb-2">Course Curriculum</h2>
				<p class="text-ak-text-secondary mb-8">{course.duration} · 2 classes/week</p>

				<div class="space-y-4">
					{#each course.curriculum as module, moduleIndex}
						<div class="bg-linear-card rounded-lg border border-ak-border-subtle overflow-hidden">
							<!-- Module Header (Clickable) -->
							<button
								onclick={() => toggleModule(moduleIndex)}
								class="w-full p-5 lg:p-6 bg-ak-deep/30 hover:bg-ak-deep/40 transition-colors flex items-start justify-between gap-3 text-left"
							>
								<div class="flex items-center gap-3 min-w-0 flex-1">
									<span class="w-8 h-8 rounded-lg bg-ak-wintergreen-dim flex items-center justify-center text-ak-wintergreen font-semibold text-sm shrink-0">
										{moduleIndex + 1}
									</span>
									<h3 class="font-display text-lg font-semibold leading-snug">{module.title}</h3>
								</div>
								<div class="flex items-center gap-2 shrink-0">
									<span class="text-sm text-ak-text-muted whitespace-nowrap">{module.lessons.length} lessons</span>
									<svg
										class="w-5 h-5 text-ak-text-muted transition-transform duration-200 {expandedModule === moduleIndex ? 'rotate-180' : ''}"
										fill="none"
										viewBox="0 0 24 24"
										stroke="currentColor"
										stroke-width="2"
									>
										<path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" />
									</svg>
								</div>
							</button>

							<!-- Lessons List (Accordion Content) -->
							{#if expandedModule === moduleIndex}
								<ul class="divide-y divide-ak-border-subtle">
									{#each module.lessons as lesson}
										<li class="px-5 lg:px-6 py-3 flex items-center justify-between text-sm">
											<div class="flex items-center gap-3">
												<svg class="w-4 h-4 text-ak-text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
													<path stroke-linecap="round" stroke-linejoin="round" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
													<path stroke-linecap="round" stroke-linejoin="round" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
												</svg>
												<span class="text-ak-text-secondary">{lesson.title}</span>
											</div>
											{#if lesson.duration}
												<span class="text-ak-text-muted">{lesson.duration}</span>
											{/if}
										</li>
									{/each}
								</ul>
							{/if}
						</div>
					{/each}
				</div>
			</div>
		</section>
	{/if}
	<!-- FAQ -->
	<section class="py-8 lg:py-12 bg-ak-deep">
		<div class="container mx-auto px-4">
			<h2 class="font-display font-bold mb-2">Frequently Asked Questions</h2>
			<p class="text-ak-text-secondary max-w-xl mb-8">Got questions? We've got answers.</p>

			<div class="max-w-2xl mx-auto space-y-4">
				<div class="bg-ak-surface-overlay rounded-lg p-5 border border-ak-border-subtle">
					<h3 class="font-semibold text-sm text-ak-text-primary mb-2">What is the course schedule?</h3>
					<p class="text-ak-text-secondary text-sm leading-relaxed">
						Courses run on weekends with live sessions. Specific schedules vary by batch. Contact us for upcoming batch timings.
					</p>
				</div>

				<div class="bg-ak-surface-overlay rounded-lg p-5 border border-ak-border-subtle">
					<h3 class="font-semibold text-sm text-ak-text-primary mb-2">Do I need prior coding experience?</h3>
					<p class="text-ak-text-secondary text-sm leading-relaxed">
						Depends on the course. Foundation courses are beginner-friendly. Advanced courses require some programming background. Check the prerequisites above.
					</p>
				</div>

				<div class="bg-ak-surface-overlay rounded-lg p-5 border border-ak-border-subtle">
					<h3 class="font-semibold text-sm text-ak-text-primary mb-2">What tools will I learn?</h3>
					<p class="text-ak-text-secondary text-sm leading-relaxed">
							You'll learn Claude Code and the BMAD tool for spec-driven development. We focus on methodology over specific tools.
						</p>
				</div>

				<div class="bg-ak-surface-overlay rounded-lg p-5 border border-ak-border-subtle">
					<h3 class="font-semibold text-sm text-ak-text-primary mb-2">Is there a certificate?</h3>
					<p class="text-ak-text-secondary text-sm leading-relaxed">
						Yes! All students who complete the course receive a certificate of completion that you can share on LinkedIn and with employers.
					</p>
				</div>

				<div class="bg-ak-surface-overlay rounded-lg p-5 border border-ak-border-subtle">
					<h3 class="font-semibold text-sm text-ak-text-primary mb-2">What if I miss a session?</h3>
					<p class="text-ak-text-secondary text-sm leading-relaxed">
						All sessions are recorded. You'll have access to recordings and can catch up at your own pace. You can also ask questions during the next live session.
					</p>
				</div>

				<div class="bg-ak-surface-overlay rounded-lg p-5 border border-ak-border-subtle">
					<h3 class="font-semibold text-sm text-ak-text-primary mb-2">Are payment plans available?</h3>
					<p class="text-ak-text-secondary text-sm leading-relaxed">
						For courses over ₹10,000, we offer EMI options. Contact us to discuss payment plan flexibility based on your situation.
					</p>
				</div>
			</div>
		</div>
	</section>
	<!-- Other Courses -->
	{#if otherCourses.length > 0}
		<section class="py-8 lg:py-12">
			<div class="container mx-auto px-4">
				<h2 class="font-display font-bold mb-2">Other Courses</h2>

				<div class="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
					{#each otherCourses as otherCourse}
						<a
							href="/courses/{otherCourse.slug}"
							class="group p-4 sm:p-5 bg-linear-card rounded-lg border border-ak-border-subtle hover:border-ak-wintergreen/50 transition-all"
						>
							<div class="flex items-center gap-2 mb-3">
								<span class="px-2 py-0.5 text-xs font-medium rounded-full {levelColors[otherCourse.level]}">
									{otherCourse.level}
								</span>
								<span class="text-xs text-ak-text-muted">{otherCourse.duration}</span>
							</div>

							<h3 class="font-display text-lg font-semibold mb-2 group-hover:text-ak-wintergreen transition-colors">
								{otherCourse.title}
							</h3>

							<p class="text-sm text-ak-text-secondary line-clamp-2">
								{otherCourse.description}
							</p>

							<div class="mt-4 text-sm font-semibold text-ak-wintergreen">
								{getPricing(otherCourse.price, pricingState.selectedCurrency)}
							</div>
						</a>
					{/each}
				</div>
			</div>
		</section>
	{/if}

	<!-- Final CTA -->
	<section class="relative py-12 lg:py-20 overflow-hidden border-t border-ak-border-subtle">
		<div class="absolute inset-0 bg-linear-dark"></div>
		<div class="absolute top-0 left-1/2 -translate-x-1/2 w-150 h-75 bg-linear-glow opacity-30 blur-3xl pointer-events-none"></div>

		<div class="relative container mx-auto px-4 text-center">
			<h2 class="font-display font-bold mb-2">Ready to <span class="text-gradient">Start Learning?</span></h2>
			<p class="text-ak-text-secondary max-w-xl mx-auto mb-8">
				Express your interest and we'll get back to you with enrollment details and upcoming batch schedules.
			</p>
			<a
				href="/contact?type=training&course={course.slug}"
				class="inline-flex items-center justify-center gap-2 px-6 py-3 min-h-12 bg-ak-wintergreen text-ak-deep font-semibold rounded-md hover:opacity-90 transition-opacity"
			>
				Express Interest
				<svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
					<path stroke-linecap="round" stroke-linejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
				</svg>
			</a>

			{#if freeSession}
				<p class="mt-5 text-sm text-ak-text-muted">
					Not ready for 10 weeks? Try the
					<a
						href={freeSession.detailRedirect ?? `/courses/${freeSession.slug}`}
						class="text-ak-wintergreen font-medium hover:opacity-80 transition-opacity"
					>
						free 90-minute Agentic AI Masterclass
					</a>
					&mdash; <LocalSessionTime cohort={freeSession.cohort} variant="full" />.
				</p>
			{/if}
		</div>
	</section>
</main>

<!-- Mobile sticky CTA bar (hidden on lg+) -->
<div class="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-ak-deep/95 backdrop-blur-md border-t border-ak-border-subtle px-4 py-3 safe-area-bottom">
	<div class="flex items-center justify-between gap-3 max-w-lg mx-auto">
		<div>
			<div class="text-xs text-ak-text-muted">Course Fee</div>
			<div class="font-display text-xl font-bold text-ak-wintergreen leading-tight">
				{getPricing(course.price, pricingState.selectedCurrency)}
			</div>
			<div class="text-xs text-ak-text-muted">+ GST</div>
		</div>
		<a
			href="/contact?type=training&course={course.slug}"
			class="flex-shrink-0 px-6 py-3 bg-ak-wintergreen text-ak-deep font-semibold rounded-md text-sm hover:opacity-90 active:opacity-80 transition-opacity touch-manipulation"
		>
			Express Interest
		</a>
	</div>
</div>

<style>
	.line-clamp-2 {
		display: -webkit-box;
		-webkit-box-orient: vertical;
		-webkit-line-clamp: 2;
		line-clamp: 2;
		overflow: hidden;
	}
</style>
